import React, { useMemo, useCallback, useState } from 'react';
import { StyleSheet, Pressable, Image, ActivityIndicator, View, Platform, ImageBackground } from 'react-native';
import { Image as ExpoImage } from 'expo-image';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import useResponsiveness from '@/hooks/useResponsiveness';
import { formatFileSize, getFileIconByMimeType, getFileTypeLabel, getFileTypeColor } from '@/constants/utils/files';
import { codeChangeStats } from '@/constants/utils/code_diff';
import MediaImagePreview from '@/components/Media/MediaImagePreview';
import { ALLOWED_INPAINTING_FILE_TYPES } from '@/constants/config';
import PropTypes from 'prop-types';
import { useVideoPlayer, VideoView } from 'expo-video';
import DownloadButton, { downloadButtonStyle } from './MediaDownloadButton';
import AudioWaveformPlayer from '@/components/ChatInput/AudioWaveformPlayer';
import Model3DCardContent from '@/components/Model3D/Model3DCardContent';
import { isMediaOutputFile } from '@/constants/utils/message';
import { downloadMedia } from '@/constants/utils/mediaDownload';
import { useToastStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';

import checkerDark from '@/assets/images/checker-dark.png';
import checkerLight from '@/assets/images/checker-light.png';


const createStyles = (colors, isXS, isLarge, fillContainer, compact) => StyleSheet.create({
  container: {
    position: 'relative',
    width: fillContainer ? '100%' : (isLarge ? (isXS ? 300 : 440) : 210),
    height: fillContainer ? '100%' : (isLarge ? (isXS ? 300 : 440) : 210),
  },
  mediaContainer: {
    backgroundColor: colors.solid_color_block,
    borderRadius: 12,
    overflow: 'hidden',
  },
  mediaContent: {
    width: '100%',
    height: '100%',
    borderRadius: 12,
    overflow: 'hidden',
  },
  documentContainer: {
    // Documents/artifacts are not square media thumbnails — use a compact
    // card sized to content. Merging onto `container` used to inherit its
    // fixed 300×300 media height, which pushed the card above the fold when
    // chat auto-scrolled to the short "I've updated…" acknowledgment.
    position: 'relative',
    width: fillContainer ? '100%' : (isLarge ? (isXS ? 300 : 360) : (isXS ? 240 : 280)),
    maxWidth: '100%',
    backgroundColor: colors.solid_color_block,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    borderRadius: 12,
    paddingVertical: compact ? 12 : 16,
    paddingHorizontal: compact ? 12 : 14,
    paddingRight: compact ? 44 : 14,
    alignItems: compact ? 'center' : 'center',
    justifyContent: compact ? 'flex-start' : 'center',
    flexDirection: compact ? 'row' : 'column',
    gap: compact ? 12 : 0,
    overflow: 'hidden',
  },
  fileContent: {
    width: '100%',
    height: '100%',
    backgroundColor: colors.solid_color_block,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  imagePreview: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  playIcon: {
    ...(Platform.OS === 'web' ? {
      filter: 'drop-shadow(0px 0px 8px rgba(0, 0, 0, .7))',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0.7,
      shadowRadius: 8,
    }),
  },
  centerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
  },
  videoFallback: {
    backgroundColor: '#14181c',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mediaCaption: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.58)',
  },
  mediaCaptionText: {
    flex: 1,
    color: '#fff',
  },
  checkerboard: {
    position: 'absolute',
    opacity: 0.5,
    height: '100%',
    width: '100%',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  checkerImage: {
    resizeMode: 'repeat',
    height: '100%',
    width: '100%',
  },
  fileIconContainer: {
    width: compact ? 44 : 80,
    height: compact ? 44 : 80,
    borderRadius: compact ? 10 : 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: compact ? 0 : 12,
    flexShrink: 0,
  },
  fileTextContainer: {
    alignSelf: compact ? 'center' : 'stretch',
    flex: compact ? 1 : undefined,
    width: compact ? undefined : '100%',
    maxWidth: '100%',
    minWidth: 0,
    alignItems: compact ? 'flex-start' : 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  fileName: {
    width: '100%',
    maxWidth: '100%',
    color: colors.text_color_primary,
    fontSize: compact ? 13 : 14,
    lineHeight: compact ? 18 : 20,
    textAlign: compact ? 'left' : 'center',
    marginBottom: 4,
    ...(Platform.OS === 'web' ? {
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
    } : {}),
  },
  fileType: {
    color: colors.text_color_secondary,
    fontSize: compact ? 12 : 13,
    textAlign: compact ? 'left' : 'center',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
  },
  fileInfoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: compact ? 'flex-start' : 'center',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 4,
    maxWidth: '100%',
  },
});

const MediaCardLarge = ({ file, onPress, onDownload, isLarge = false, isDownloadable = false, isEditable = false, onEdit, fillContainer = false, compact = false }) => {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const { addToast } = useToastStore(state => state.actions);
  const styles = useMemo(() => createStyles(colors, isXS, isLarge, fillContainer, compact), [colors, isXS, isLarge, fillContainer, compact]);
  const fileIcon = getFileIconByMimeType(file?.mimeType || file?.type || file?.fileType || file?.extension);
  const isVideo = file?.fileType === "video" || file.type?.startsWith('video/') || isMediaOutputFile(file, 'video');
  // Extension/mime win over a wrong image fileType so Work mp4s never render
  // as blank image thumbnails.
  const isImage = !isVideo && (
    file?.fileType === "image" || file.type?.startsWith('image/') || isMediaOutputFile(file, 'image')
  );
  const isAudio = file?.fileType === "audio" || file.type?.startsWith?.('audio/') || isMediaOutputFile(file, 'audio');
  // Work-mode GLBs are often stored as fileType: 'file' + application/octet-stream
  // because the Meshy download never went through the chat 3D pipeline.
  const is3D = file?.fileType === '3d' || isMediaOutputFile(file, '3d');
  const isMedia = isImage || isVideo || isAudio || is3D;
  const [isHovered, setIsHovered] = React.useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const canDownload = isDownloadable && (!!file.cdnPath || typeof onDownload === 'function');
  const [videoThumbFailed, setVideoThumbFailed] = useState(false);

  // Track hover per source (card body vs. the overlay action button) instead
  // of a single boolean. On web, hovering the nested DownloadButton ends the
  // container's contained-hover (`onHoverOut` fires as the cursor enters the
  // button), which used to hide the button right as it was being approached.
  // Keeping separate flags lets either source hold the hovered state.
  const hoverSourcesRef = React.useRef({ card: false, action: false });
  const setHoverSource = useCallback((source, value) => {
    if (hoverSourcesRef.current[source] === value) return;
    hoverSourcesRef.current[source] = value;
    const next = hoverSourcesRef.current.card || hoverSourcesRef.current.action;
    setIsHovered((prev) => (prev === next ? prev : next));
  }, []);
  const handleCardHoverIn = useCallback(() => setHoverSource('card', true), [setHoverSource]);
  const handleCardHoverOut = useCallback(() => setHoverSource('card', false), [setHoverSource]);
  const handleActionHoverChange = useCallback((value) => setHoverSource('action', value), [setHoverSource]);
  const fileTypeColor = getFileTypeColor(file?.mimeType || file?.type || file?.fileType || file?.extension);
  const fileTypeLabel = getFileTypeLabel(file?.mimeType || file?.type || file?.fileType || file?.extension);
  const changeStats = codeChangeStats(file);

  // 
  // turn off for now
  // 
  isEditable = false;
  // ------------------

  const canEdit = useMemo(() => {
    if (!isEditable) return false;
    const type = file?.fileType || file?.type;
    return ALLOWED_INPAINTING_FILE_TYPES.some(t => type === t || type?.startsWith?.(t + '/'));
  }, [isEditable, file]);

  const naturalImageWidth = file?.metadata?.image?.width;
  const naturalImageHeight = file?.metadata?.image?.height;
  const naturalVideoWidth = file?.metadata?.video?.width;
  const naturalVideoHeight = file?.metadata?.video?.height;

  const baseContainerWidth = useMemo(() => (isLarge ? (isXS ? 300 : 440) : 210), [isLarge, isXS]);
  const computedAspectHeight = useMemo(() => {
    // When fillContainer is true, parent controls dimensions
    if (fillContainer) return null;
    if (!isLarge) return null;
    if (isImage && naturalImageWidth && naturalImageHeight) {
      return Math.round(baseContainerWidth * (naturalImageHeight / naturalImageWidth));
    }
    if (isVideo && naturalVideoWidth && naturalVideoHeight) {
      return Math.round(baseContainerWidth * (naturalVideoHeight / naturalVideoWidth));
    }
    return null;
  }, [fillContainer, isLarge, isImage, isVideo, naturalImageWidth, naturalImageHeight, naturalVideoWidth, naturalVideoHeight, baseContainerWidth]);
  const audioPreferredHeight = useMemo(() => {
    if (!isAudio) return null;
    if (isLarge) {
      if (isAudio) return 100;
      return isXS ? 120 : 140;
    }
    return 100;
  }, [isAudio, isLarge, isXS]);

  const imageUrl = useMemo(() => {
    if (file.cdnPath) {
      return file.cdnPath;
    }
    if (file.url) {
      return file.url;
    }
    if (isImage && (file instanceof File || file.previewFile instanceof File)) {
      const fileToPreview = file instanceof File ? file : file.previewFile;
      return URL.createObjectURL(fileToPreview);
    }
    return null;
  }, [file, isImage]);

  const videoSourceUrl = React.useMemo(() => {
    if (!isVideo) return null;
    if (file?.cdnPath) return file.cdnPath;
    if (file?.url) return file.url;
    return null;
  }, [file, isVideo]);

  const videoThumbnailUrl = React.useMemo(() => {
    if (!isVideo) return null;
    if (file?.thumbnailPath) return file.thumbnailPath;
    return null;
  }, [file, isVideo]);

  React.useEffect(() => {
    setVideoThumbFailed(false);
  }, [videoThumbnailUrl, file?.id, file?.cdnPath]);

  const showVideoThumb = !!(videoThumbnailUrl && !videoThumbFailed);

  const audioSourceUrl = React.useMemo(() => {
    if (!isAudio) return null;
    if (file?.cdnPath) return file.cdnPath;
    if (file?.url) return file.url;
    return null;
  }, [file, isAudio]);

  const player = useVideoPlayer(isVideo && !showVideoThumb ? (videoSourceUrl ?? null) : null, (p) => {
    if (!isVideo) return;
    p.loop = false;
    p.muted = false; // Explicitly enable audio playback on web
    p.pause();
  });

  React.useEffect(() => {
    return () => {
      if (imageUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  const audioPlayerRef = React.useRef(null);

  const handlePress = useCallback(async () => {
    try {
      await audioPlayerRef.current?.pause?.();
    } catch (e) {}
    if (onPress) {
      onPress(file);
    }
  }, [file, onPress]);

  const handleEdit = useCallback(() => {
    if (onEdit) {
      onEdit(file);
    }
  }, [onEdit, file]);

  const handleDownload = useCallback(async (e) => {
    e?.stopPropagation?.();
    if ((!file.cdnPath && !onDownload) || isDownloading) return;

    const extension = file.extension
      ? (String(file.extension).startsWith('.') ? file.extension : `.${file.extension}`)
      : '';
    const filename = file.name || `file-${Date.now()}${extension}`;

    setIsDownloading(true);
    try {
      if (onDownload) {
        const result = await onDownload(file);
        if (result?.cancelled) return;
        if (result?.success === false) throw new Error(result.message || t('media.downloadFailed'));
        addToast?.(t('media.downloadComplete'), 'success');
        return;
      }
      await downloadMedia(file.cdnPath, filename, {
        onSuccess: (message) => {
          // Web saves to the browser Downloads folder; native photos-compatible
          // media lands in Photos. Prefer the util's message so share-sheet
          // flows don't claim "Saved to Photos" incorrectly.
          const toastMsg = Platform.OS === 'web'
            ? t('media.downloadComplete')
            : (message === 'Saved to Photos' ? t('media.savedToPhotos') : t('media.downloadComplete'));
          addToast?.(toastMsg, 'success');
        },
        onError: (message) => addToast?.(message || t('media.downloadFailed'), 'error'),
      });
    } catch (error) {
      addToast?.(error?.message || t('media.downloadFailed'), 'error');
    } finally {
      setIsDownloading(false);
    }
  }, [file, onDownload, isDownloading, addToast, t]);

  const renderLoadingOverlay = () => {
    if (!file.isUploading) return null;
    return (
      <View style={styles.loadingOverlay}>
        <ActivityIndicator size="large" color={colors.text_color_alt} />
      </View>
    );
  };

  // Audio and 3D already have inline controls (seek / orbit). A card-level
  // press would steal those gestures — Work opened the lightbox or started
  // a download when the user was trying to scrub or rotate.
  const isInlineInteractive = isAudio || is3D;
  const cardPress = onPress && !isInlineInteractive ? handlePress : undefined;
  // Keep native Download/Edit targets outside the full-card opener; macOS
  // does not reliably honor zIndex when hit-testing overlapping siblings.
  const nativeActionWidth = Platform.OS === 'macos'
    ? (Number(!!(canDownload)) + Number(!!canEdit)) * 40
    : 0;
  // Open and Download are sibling controls. A button wrapping another
  // button makes the actions ambiguous to keyboard and screen-reader users.
  const openControl = cardPress ? (
    <Pressable
      style={[StyleSheet.absoluteFillObject, { borderRadius: 12, zIndex: 1 }, nativeActionWidth > 0 && { right: nativeActionWidth + 8 }]}
      onPress={cardPress}
      accessibilityRole="button"
      accessibilityLabel={`Open ${file?.name || fileTypeLabel}`}
    />
  ) : null;

  if (isMedia) {
    return (
      <View
        style={[
          styles.container,
          styles.mediaContainer,
          computedAspectHeight ? { height: computedAspectHeight } : (isAudio && audioPreferredHeight ? { height: audioPreferredHeight } : null),
          isAudio && fillContainer ? { width: '100%', height: audioPreferredHeight || 108 } : null,
        ]}
        onMouseEnter={handleCardHoverIn}
        onMouseLeave={handleCardHoverOut}
        onHoverIn={handleCardHoverIn}
        onHoverOut={handleCardHoverOut}
      >
        <View style={styles.mediaContent}>
          <View style={styles.fileContent}>
            {isImage && !file?.blurhash && (
              <ImageBackground
                source={isDarkTheme(theme) ? checkerDark : checkerLight}
                style={styles.checkerboard}
                imageStyle={styles.checkerImage}
                resizeMode="repeat"
              />
            )}
            {isImage && imageUrl ? (
              <MediaImagePreview
                uri={imageUrl}
                file={file}
                style={styles.imagePreview}
                contentFit="cover"
                placeholder={file?.blurhash ? { blurhash: file?.blurhash } : undefined}
                transition={200}
                preferThumbnail
              />
            ) : isVideo ? (
              showVideoThumb ? (
                <>
                  {file?.blurhash ? (
                    <ExpoImage
                      source={{ uri: videoThumbnailUrl }}
                      style={styles.imagePreview}
                      contentFit="cover"
                      placeholder={file?.blurhash ? { blurhash: file?.blurhash } : undefined}
                      transition={150}
                      onError={() => setVideoThumbFailed(true)}
                    />
                  ) : (
                    <Image
                      source={{ uri: videoThumbnailUrl }}
                      style={styles.imagePreview}
                      onError={() => setVideoThumbFailed(true)}
                    />
                  )}
                  <View style={styles.centerOverlay}>
                    <Icon name="play" size={28} color='#fff' style={styles.playIcon} />
                  </View>
                </>
              ) : videoSourceUrl ? (
                <>
                  <VideoView
                    player={player}
                    style={styles.imagePreview}
                    nativeControls={false}
                    allowsFullscreen={false}
                    allowsPictureInPicture={false}
                    contentFit="cover"
                    crossOrigin="anonymous"
                  />
                  <View style={styles.centerOverlay}>
                    <Icon name="play" size={28} color='#fff' style={styles.playIcon} />
                  </View>
                </>
              ) : (
                <View style={[styles.imagePreview, styles.videoFallback]}>
                  <Icon name="play" size={28} color='#fff' style={styles.playIcon} />
                </View>
              )
            ) : isAudio ? (
              <AudioWaveformPlayer
                ref={audioPlayerRef}
                sourceUrl={audioSourceUrl}
                size={fillContainer ? 520 : baseContainerWidth}
                file={file}
              />
            ) : is3D ? (
              <Model3DCardContent
                file={file}
                viewerOnly={false}
                autoActivate={!file?.thumbnailPath && !file?.thumbnail}
              />
            ) : (
              <Icon name="image" size={48} color={colors.text_color_secondary} />
            )}
          </View>
        </View>
        {isVideo && !!file?.name && (
          <View style={styles.mediaCaption} pointerEvents="none">
            <Icon name="film" size={11} color="#fff" />
            <Text size="xxs" weight="medium" color="alt" numberOfLines={1} style={styles.mediaCaptionText}>
              {file.name}
            </Text>
          </View>
        )}
        {renderLoadingOverlay()}
        {openControl}
        {canDownload && (
          <DownloadButton
            isHovered={isHovered}
            onPress={handleDownload}
            isLoading={isDownloading}
            onHoverChange={handleActionHoverChange}
            label={`Download ${file?.name || fileTypeLabel}`}
          />
        )}
        {canEdit && isHovered && (
          <Pressable
            style={[downloadButtonStyle.downloadButton, { right: canDownload ? 48 : 8, opacity: 1 }]}
            onPress={handleEdit}
            accessibilityRole="button"
            accessibilityLabel={`Edit ${file?.name || fileTypeLabel}`}
            onMouseEnter={() => handleActionHoverChange(true)}
            onMouseLeave={() => handleActionHoverChange(false)}
            onHoverIn={() => handleActionHoverChange(true)}
            onHoverOut={() => handleActionHoverChange(false)}
          >
            <Icon name="pen-to-square" size={14} color="#fff" />
          </Pressable>
        )}
      </View>
    );
    // : todo :
    // show audio transcript
  }

  return (
    <View
      style={styles.documentContainer}
      onMouseEnter={handleCardHoverIn}
      onMouseLeave={handleCardHoverOut}
      onHoverIn={handleCardHoverIn}
      onHoverOut={handleCardHoverOut}
    >
      <View style={[styles.fileIconContainer, { backgroundColor: fileTypeColor + '20' }]}> 
        <Icon name={fileIcon} size={compact ? 22 : 48} color={fileTypeColor} />
      </View>
      <View style={styles.fileTextContainer}>
        <Text
          style={styles.fileName}
          numberOfLines={2}
          ellipsizeMode="tail"
          accessibilityLabel={file?.name}
        >
          {file?.name || 'Untitled'}
        </Text>
        <View style={styles.fileInfoContainer}>
          <Text style={styles.fileType}>{fileTypeLabel}</Text>
          <Text style={styles.fileType}>•</Text>
          <Text style={styles.fileType}>{formatFileSize(file?.sizeInBytes ?? file?.size)}</Text>
          {changeStats ? (
            <>
              <Text style={styles.fileType}>•</Text>
              <Text style={[styles.fileType, { color: '#22C55E' }]}>+{changeStats.added}</Text>
              <Text style={[styles.fileType, { color: '#EF4444' }]}>−{changeStats.removed}</Text>
            </>
          ) : null}
          {file.isUploading && (
            <>
              <Text style={styles.fileType}>•</Text>
              <ActivityIndicator size="small" color={colors.text_color_secondary} />
            </>
          )}
        </View>
      </View>
      {openControl}
      {canDownload && (
        <DownloadButton
          isHovered={isHovered}
          onPress={handleDownload}
          isLoading={isDownloading}
          onHoverChange={handleActionHoverChange}
          label={`Download ${file?.name || fileTypeLabel}`}
        />
      )}
      {canEdit && (
        <Pressable
          style={[downloadButtonStyle.downloadButton, { right: canDownload ? 48 : 8, opacity: 1 }]}
          onPress={handleEdit}
          accessibilityRole="button"
          accessibilityLabel={`Edit ${file?.name || fileTypeLabel}`}
          onMouseEnter={() => handleActionHoverChange(true)}
          onMouseLeave={() => handleActionHoverChange(false)}
          onHoverIn={() => handleActionHoverChange(true)}
          onHoverOut={() => handleActionHoverChange(false)}
        >
          <Icon name="pen-to-square" size={14} color="#fff" />
        </Pressable>
      )}
    </View>
  );
};

MediaCardLarge.propTypes = {
  file: PropTypes.shape({
    name: PropTypes.string,
    cdnPath: PropTypes.string,
    type: PropTypes.string,
    fileType: PropTypes.string,
    extension: PropTypes.string,
    mimeType: PropTypes.string,
    size: PropTypes.number,
    sizeInBytes: PropTypes.number,
    isUploading: PropTypes.bool,
    thumbnailPath: PropTypes.string,
    previewFile: PropTypes.any,
    blurhash: PropTypes.string,
  }).isRequired,
  onPress: PropTypes.func,
  onDownload: PropTypes.func,
  isLarge: PropTypes.bool,
  isDownloadable: PropTypes.bool,
  isEditable: PropTypes.bool,
  onEdit: PropTypes.func,
  fillContainer: PropTypes.bool,
  compact: PropTypes.bool,
};

export default MediaCardLarge;
